import { JsonSyntax } from './JsonSyntax';
import { MissingFieldsDetailsSchema } from './types';
import type { MissingFieldEntry } from './types';

/**
 * Renders error/warning details as a human-readable table when the shape
 * is known (e.g. MISSING_FIELDS), falls back to JsonSyntax otherwise.
 */
export function ErrorDetailView({ errorCode, details }: { errorCode?: string; details: Record<string, unknown> }) {
  // MISSING_FIELDS: render as a table of missing field names + reasons
  const parsed = MissingFieldsDetailsSchema.safeParse(details);
  const missing = parsed.success ? parsed.data.missing : undefined;
  if ((errorCode === 'MISSING_FIELDS' || errorCode === 'MISSING_REQUIRED_FIELDS') && missing !== undefined) {
    return (
      <div className="rounded-lg border border-border-secondary overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-bg-secondary text-left text-icon-subtle uppercase">
            <tr>
              <th className="px-3 py-1.5">Field</th>
              <th className="px-3 py-1.5">Type</th>
              <th className="px-3 py-1.5">Reason</th>
              <th className="px-3 py-1.5">Source</th>
              <th className="px-3 py-1.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-tertiary">
            {missing.map((m: MissingFieldEntry) => (
              <tr key={m.field} className="bg-tangerine-50/50">
                <td className="px-3 py-1.5 font-mono text-content-primary">{m.field}</td>
                <td className="px-3 py-1.5 text-icon-subtle">{m.type ?? '—'}</td>
                <td className="px-3 py-1.5 text-content-tertiary">{m.reason ?? '—'}</td>
                <td className="px-3 py-1.5">
                  <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    m.source === 'card_slot' ? 'bg-blueberry-50 text-blueberry-600' :
                    m.source === 'action_form' ? 'bg-ube-50 text-ube-600' :
                    'bg-bg-tertiary text-icon-subtle'
                  }`}>
                    {m.source ?? 'unknown'}
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  {m.legacyPresent === true ? (
                    <span className="inline-flex items-center rounded-full bg-bg-red-light px-1.5 py-0.5 text-[10px] font-medium text-content-danger">
                      mapping bug
                    </span>
                  ) : m.legacyPresent === false ? (
                    <span className="inline-flex items-center rounded-full bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-icon-subtle">
                      source gap
                    </span>
                  ) : (
                    <span className="text-icon-subtle">&mdash;</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: render as syntax-highlighted JSON
  return <JsonSyntax data={details} />;
}
